import ApiWithLogging from '../utils/apiWithLogging';
import { RentalHoldInfo } from '../types/rentalHold';

// ประเภทซิม (จากข้อมูล CSV)
export enum SimType {
  PREPAID = 'PREPAID',     // เติมเงิน
  POSTPAID = 'POSTPAID',   // รายเดือน
  YEARLY = 'YEARLY',       // รายปี (user feedback Suraluck 2026-08-25)
  CORPORATE = 'CORPORATE', // องค์กร
  OTHER = 'OTHER'          // อื่นๆ
}

// สถานะซิม — BE may return any of these strings (case + Thai variants supported defensively)
export enum SimStatus {
  AVAILABLE = 'AVAILABLE', // พร้อมใช้งาน
  ACTIVE = 'ACTIVE',       // ใช้งานอยู่ (alias for IN_USE)
  IN_USE = 'IN_USE',       // กำลังใช้งาน
  INACTIVE = 'INACTIVE',   // ไม่ได้ใช้งาน
  SUSPENDED = 'SUSPENDED', // ระงับการใช้งาน
  EXPIRED = 'EXPIRED',     // หมดอายุ
  // ใบงานเตรียมเครื่อง (PO 2026-09-08): ซิมถูกกันไว้ให้ดีลที่ปิดแล้วแต่ยังไม่ส่งมอบ
  // ระบบตั้งค่านี้เองตอนจับคู่ซิมเข้าใบงาน — ฟอร์มแก้ไขซิมจึงไม่มีให้เลือกเอง
  RESERVED = 'RESERVED',   // จองไว้ (ใบงานเตรียมเครื่อง)
  // ยืมซิม (23 ก.ย. 2026): ซิมถูกยืมไปใช้ภายในตามใบเบิก-ยืม — ระบบตั้งเอง คืนที่หน้าใบเบิก-ยืม
  ISSUED = 'ISSUED'        // เบิกไปใช้ภายใน
}

// Interface สำหรับ Product ที่แสดงใน Device
export interface ProductInDevice {
  id: number;
  name: string;
}

// Interface สำหรับ Device ที่แสดงใน Sim
export interface DeviceInSim {
  id: number;
  code: string;
  serial_number?: string;
  product?: ProductInDevice;
}

// Billing Cycle enum (for SIM packages)
export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}

// ประเภทข้อมูลซิมการ์ด
export interface Sim {
  id: number;
  code: string;               // รหัสซิม
  phone_number: string;       // เบอร์โทรศัพท์
  serial_number?: string;     // หมายเลขซีเรียล
  operator: string;           // ค่ายมือถือ (True, AIS, DTAC)
  sim_type: SimType;          // ประเภทซิม (เติมเงิน, รายเดือน)
  monthly_fee?: number;       // ค่าบริการรายเดือน
  activation_code?: string;   // รหัส Activate
  registration_name?: string; // ชื่อผู้ลงทะเบียน
  registered_by_user_id?: number; // รหัสผู้ลงทะเบียน
  activation_date?: string;   // วันที่เปิดใช้งาน
  expiry_date?: string;       // วันหมดอายุ
  status: SimStatus;          // สถานะ
  notes?: string;             // หมายเหตุ
  device_id?: number;         // ID ของอุปกรณ์ที่ใช้ซิมนี้ (ถ้ามี)
  device?: DeviceInSim;       // ข้อมูลอุปกรณ์ที่ใช้ซิมนี้ (จาก backend)
  device_code?: string;       // รหัสอุปกรณ์ที่ใช้ซิมนี้ (สำหรับ mock data)

  // ข้อมูลแพ็กเกจ — sim_package_id อ้าง master; package_name เป็น snapshot ที่ BE set ให้
  sim_package_id?: number | null; // FK → sim_packages.id
  package_name?: string;      // snapshot ชื่อแพ็กเกจ (= sim_packages.name)
  speed_mbps?: number;        // ความเร็ว Mbps
  billing_cycle?: BillingCycle; // รอบบิล (MONTHLY/YEARLY)
  yearly_cost?: number;       // ค่าใช้จ่ายรายปี

  created_by?: {              // ข้อมูลผู้สร้าง
    id: number;
    username: string;
    full_name?: string;
  };
  updated_by?: {              // ข้อมูลผู้แก้ไขล่าสุด
    id: number;
    username: string;
    full_name?: string;
  };
  created_at: string;
  updated_at?: string;

  // ด่าน L2 ฝั่งซิม (Lane D) — "ยังมีใบตามเก็บที่ยังไม่ปิด" · optional เพราะ backend รุ่นเก่า
  // ไม่ส่งมา และ payload ที่ไม่มีค่าต้องอ่านว่า "เลือกได้" (ดู utils/simAvailability)
  has_pending_return?: boolean;
  pending_return_count?: number;

  // Fix ค (20 ก.ย. 2026) — "พร้อมใช้งานแต่ยังถูกล็อก" ด้วยสัญญาที่ยังไม่จบ (DRAFT/ACTIVE/
  // PENDING_CANCELLATION) — null เมื่อไม่ล็อก · ดู types/rentalHold.ts + API_CONTRACT.md ค-1
  rental_hold?: RentalHoldInfo | null;

  // ยืมซิม (23 ก.ย. 2026) — มีค่าเมื่อ status = ISSUED เท่านั้น (mirror ฝั่งอุปกรณ์ types/device.ts)
  issued_to_display?: string | null;
  current_stock_issue_id?: number | null;
  current_stock_issue_number?: string | null;
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขซิม
export interface SimForm {
  code?: string;
  phone_number: string;
  serial_number?: string;
  operator: string;
  sim_type: SimType;
  monthly_fee?: number;
  activation_code?: string;
  registration_name?: string;
  registered_by_user_id?: number;
  activation_date?: string;
  expiry_date?: string;
  status?: SimStatus;
  notes?: string;
  device_id?: number;

  // แพ็กเกจซิม — เลือกจาก master (sim_packages); BE จะ set package_name = sim_packages.name เอง
  sim_package_id?: number | null;
  package_name?: string;        // snapshot ชื่อแพ็กเกจ (BE เป็นผู้ set; ไม่ใช่ input ในฟอร์มแล้ว)
  speed_mbps?: number;
  billing_cycle?: BillingCycle;
  yearly_cost?: number;
}

// พารามิเตอร์สำหรับการดึงข้อมูลซิม
export interface GetSimsParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  operator?: string;
  sim_type?: SimType;
  status?: SimStatus;
  expiry_soon?: boolean;  // ซิมที่ใกล้หมดอายุ
  days_to_expiry?: number; // จำนวนวันก่อนหมดอายุ
  device_id?: number;      // กรองตามอุปกรณ์ที่ใช้ซิม
}

// การตอบกลับจากการดึงข้อมูลซิม
export interface GetSimsResponse {
  items: Sim[];
  total: number;
  page: number;
  limit: number;
}

// การใช้งานปัจจุบันของซิม — GET /sims/{id}/usage (user feedback Tiwakorn #309, 2026-08-31)
// ผู้ถือซิม = rental_item ล่าสุดใน rental ที่ยังถือ inventory (ACTIVE / PENDING_CANCELLATION)
export interface SimUsageCurrent {
  rental_id: number;
  rental_number?: string | null;
  rental_status?: string | null;      // ACTIVE | PENDING_CANCELLATION
  company_id?: number | null;
  company_name?: string | null;
  site_id?: number | null;
  site_name?: string | null;
  rental_item_id: number;
  device_id?: number | null;
  device_code?: string | null;
  device_serial_number?: string | null;
  device_model?: string | null;
  since?: string | null;              // rental_item.created_at
}

export interface SimUsage {
  sim_id: number;
  phone_number?: string | null;
  status?: SimStatus | string | null;
  in_use: boolean;
  current?: SimUsageCurrent | null;
  // สถานะซิมไม่สอดคล้องกับผู้ถือ (IN_USE แต่ไม่มีใครถือ / AVAILABLE แต่มีคนถือ) = ข้อมูลอาจค้าง
  status_mismatch: boolean;
  // ด่าน L2 — ใบตามเก็บที่ยังไม่ปิดของซิมใบนี้ (optional: backend รุ่นเก่าไม่ส่ง)
  has_pending_return?: boolean;
  pending_return_count?: number;
  // Fix ค (20 ก.ย. 2026) — Addenda: GET /sims/{id}/usage ก็ส่ง rental_hold มาด้วย
  rental_hold?: RentalHoldInfo | null;
}

// บริการจัดการข้อมูลซิมการ์ด
const simService = {
  /**
   * BUG-S2207-002: ดึงซิม "ทุกหน้า" สำหรับ picker ที่กรองในเครื่อง — `/sims/` ตั้งเพดาน limit 1000
   * (กันโหลดหนัก) · dev มีซิม 1,582 ใบ หน้าเดียวจึงตกไป 582 ใบ = ซิมว่างเลือกไม่ได้โดยไม่มีอะไรบอก
   * วนหน้า 1000 จนครบ total (กันวนไม่รู้จบด้วย maxPages)
   */
  async getAllSims(params: Omit<GetSimsParams, 'page' | 'limit'> = {}, maxPages = 20): Promise<GetSimsResponse> {
    const limit = 1000;
    const first = await this.getSims({ ...params, page: 1, limit });
    const items = [...(first?.items || [])];
    const total = (first as any)?.total ?? items.length;
    for (let page = 2; page <= maxPages && items.length < total; page++) {
      const next = await this.getSims({ ...params, page, limit });
      const batch = next?.items || [];
      if (!batch.length) break;
      items.push(...batch);
      if (batch.length < limit) break;
    }
    return { ...(first as any), items, total } as GetSimsResponse;
  },

  // ดึงรายการซิมทั้งหมด
  async getSims(params: GetSimsParams = {}): Promise<GetSimsResponse> {
    try {
      // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ
      const queryParams: any = {
        page: params.page || 1,
        limit: params.limit || 10
      };
      
      // เพิ่มเฉพาะ parameters ที่มีค่า
      if (params.sort !== undefined) queryParams.sort = params.sort;
      if (params.order !== undefined) queryParams.order = params.order;
      if (params.search !== undefined) queryParams.search = params.search;
      if (params.operator !== undefined) queryParams.operator = params.operator;
      if (params.sim_type !== undefined) queryParams.sim_type = params.sim_type;
      if (params.status !== undefined) queryParams.status = params.status;
      if (params.expiry_soon !== undefined) queryParams.expiry_soon = params.expiry_soon;
      if (params.days_to_expiry !== undefined) queryParams.days_to_expiry = params.days_to_expiry;
      if (params.device_id !== undefined) queryParams.device_id = params.device_id;
      
      // Debug logging removed (BUG-168 FIX)
      
      // ส่ง request ไปยัง API
      const response = await ApiWithLogging.get<GetSimsResponse>('/sims/', 
        { params: queryParams },
        {
          service: 'simService',
          action: 'view_sims_list',
          entityType: 'sim'
        }
      );
      
      return response;
    } catch (error: any) {
      console.error('Error in getSims service:', error);

      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // ดึงซิมที่ใกล้หมดอายุ
  async getExpiringSims(days: number = 30): Promise<Sim[]> {
    try {
      const response = await ApiWithLogging.get<Sim[]>('/sims/expiring', 
        { params: { days } },
        {
          service: 'simService',
          action: 'view_expiring_sims',
          entityType: 'sim',
          details: { days }
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getExpiringSims service:', error);
      throw error;
    }
  },

  // ดึงข้อมูลซิมตาม id
  async getSim(id: number): Promise<Sim> {
    try {
      const response = await ApiWithLogging.get<Sim>(`/sims/${id}`, {},
        {
          service: 'simService',
          action: 'view_sim_detail',
          entityType: 'sim',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getSim service:', error);
      throw error;
    }
  },

  // การใช้งานปัจจุบันของซิม (สัญญาเช่า/บริษัท/โครงการ/อุปกรณ์ที่ถือซิมอยู่)
  async getSimUsage(id: number): Promise<SimUsage> {
    try {
      const response = await ApiWithLogging.get<SimUsage>(`/sims/${id}/usage`, {},
        {
          service: 'simService',
          action: 'view_sim_usage',
          entityType: 'sim',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getSimUsage service:', error);
      throw error;
    }
  },

  // สร้างซิมใหม่
  async createSim(data: SimForm): Promise<Sim> {
    try {
      // ทำความสะอาดข้อมูลก่อนส่ง - เอาฟิลด์ที่เป็น null/undefined ออก
      const cleanData: any = {};
      
      Object.keys(data).forEach(key => {
        const value = (data as any)[key];
        // เก็บเฉพาะฟิลด์ที่มีค่า (ไม่ใช่ null หรือ undefined หรือ empty string หรือ 0 สำหรับ registered_by_user_id)
        if (value !== null && value !== undefined && value !== '') {
          // สำหรับ registered_by_user_id ถ้าเป็น 0 ก็ไม่ส่ง
          if (key === 'registered_by_user_id' && (value === 0 || value === '0')) {
            return;
          }
          cleanData[key] = value;
        }
      });
      
      // บังคับให้มีฟิลด์จำเป็น
      if (!cleanData.phone_number) {
        throw new Error('Phone number is required');
      }
      
      // เพิ่ม code ถ้ามี
      if (data.code) {
        cleanData.code = data.code;
      }
      
      const response = await ApiWithLogging.post<Sim>('/sims/', cleanData, {},
        {
          service: 'simService',
          action: 'create_sim',
          entityType: 'sim',
          details: {
            phone_number: cleanData.phone_number,
            operator: cleanData.operator,
            sim_type: cleanData.sim_type
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in createSim service:', error);
      
      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // สร้างซิมหลายเบอร์พร้อมกันจากรายการเบอร์ (สำหรับทีม stock) — ATOMIC
  async createSimsBulk(data: any): Promise<{ created_count: number; sims: Array<{ id: number; code?: string; phone_number?: string }> }> {
    // ทำความสะอาด: เก็บเฉพาะฟิลด์ที่มีค่า (phone_numbers เป็น array จะถูกเก็บไว้)
    const cleanData: any = {};
    Object.keys(data).forEach((key) => {
      const value = (data as any)[key];
      if (value !== null && value !== undefined && value !== '') {
        if (key === 'registered_by_user_id' && (value === 0 || value === '0')) return;
        cleanData[key] = value;
      }
    });
    const response = await ApiWithLogging.post<{ created_count: number; sims: Array<{ id: number; code?: string; phone_number?: string }> }>(
      '/sims/bulk',
      cleanData,
      {},
      {
        service: 'simService',
        action: 'bulk_create_sims',
        entityType: 'sim',
        details: { count: Array.isArray(data?.phone_numbers) ? data.phone_numbers.length : undefined }
      }
    );
    return response;
  },

  // แก้ไขข้อมูลซิม
  // BUG-S162-P1 rollout (S410): ส่ง updated_at ที่ฟอร์มเห็นล่าสุดเป็น `ifMatch`
  // เพื่อเปิด optimistic lock ฝั่ง backend (stale → 409) · ไม่ส่ง = พฤติกรรมเดิม
  async updateSim(id: number, data: SimForm, ifMatch?: string): Promise<Sim> {
    try {
      // ลบ device_id ออกเพราะ backend ไม่รองรับ และปรับ datetime fields
      const { device_id, ...rawData } = data;
      
      // สร้าง clean data object โดยลบ empty values ออก
      const updateData: any = {};
      
      // เฉพาะ fields ที่มีค่าจริงเท่านั้น
      if (rawData.phone_number?.trim()) updateData.phone_number = rawData.phone_number.trim();
      if (rawData.serial_number?.trim()) updateData.serial_number = rawData.serial_number.trim();
      if (rawData.operator?.trim()) updateData.operator = rawData.operator.trim();
      if (rawData.sim_type) updateData.sim_type = rawData.sim_type;
      if (rawData.monthly_fee !== null && rawData.monthly_fee !== undefined && String(rawData.monthly_fee) !== '') {
        updateData.monthly_fee = parseFloat(String(rawData.monthly_fee));
      }
      if (rawData.activation_code?.trim()) updateData.activation_code = rawData.activation_code.trim();
      if (rawData.registration_name?.trim()) updateData.registration_name = rawData.registration_name.trim();
      if (rawData.registered_by_user_id) updateData.registered_by_user_id = rawData.registered_by_user_id;
      if (rawData.activation_date?.trim()) {
        updateData.activation_date = new Date(rawData.activation_date).toISOString();
      }
      if (rawData.expiry_date?.trim()) {
        updateData.expiry_date = new Date(rawData.expiry_date).toISOString();
      }
      if (rawData.status) updateData.status = rawData.status;
      // "หมายเหตุ" มีช่องกรอกจริงบนฟอร์ม จึงต้อง "ล้างค่าได้" ด้วย — guard แบบ truthiness เดิม
      // (`if (notes?.trim())`) ทำให้ผู้ใช้ลบข้อความในช่องแล้วกดบันทึก กลายเป็น no-op เงียบ ๆ
      // ค่าเก่าเด้งกลับมาเหมือนไม่ได้กด · ส่งเมื่อ field ถูกระบุมา (รวมสตริงว่าง = ตั้งใจล้าง)
      if (rawData.notes !== undefined && rawData.notes !== null) {
        updateData.notes = rawData.notes.trim();
      }

      // แพ็กเกจซิม (master) — 🐛 เดิม updateSim ไม่ได้ map sim_package_id → แก้แพ็กเกจตอน edit ไม่ติด
      // ส่งทุกครั้งที่ field ถูกระบุใน payload (รวม null = ล้างค่า) เพื่อให้เปลี่ยน/ถอดแพ็กเกจได้
      if (rawData.sim_package_id !== undefined) {
        updateData.sim_package_id = rawData.sim_package_id;
      }
      // package_name = snapshot; BE เป็นผู้ set จาก sim_packages.name → ไม่ map จากฟอร์ม
      if (rawData.speed_mbps !== null && rawData.speed_mbps !== undefined) {
        updateData.speed_mbps = parseInt(String(rawData.speed_mbps));
      }
      if (rawData.billing_cycle) updateData.billing_cycle = rawData.billing_cycle;
      if (rawData.yearly_cost !== null && rawData.yearly_cost !== undefined) {
        updateData.yearly_cost = parseFloat(String(rawData.yearly_cost));
      }

      const response = await ApiWithLogging.put<Sim>(`/sims/${id}`, updateData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'simService',
          action: 'update_sim',
          entityType: 'sim',
          entityId: id,
          details: {
            phone_number: data.phone_number,
            status: data.status
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in updateSim service:', error);
      
      // แสดง error details จาก server
      if (error.response?.data) {
        console.error('Server error response:', error.response.data);
      }
      
      throw error;
    }
  },

  // ลบซิม
  async deleteSim(id: number): Promise<{success: boolean; message: string}> {
    try {
      const response = await ApiWithLogging.delete<{success: boolean; message: string}>(`/sims/${id}`, {},
        {
          service: 'simService',
          action: 'delete_sim',
          entityType: 'sim',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in deleteSim service:', error);
      throw error;
    }
  },

  // ส่งออกข้อมูลซิมเป็น Excel
  async exportToExcel(params: GetSimsParams = {}): Promise<Blob> {
    try {
      const response = await ApiWithLogging.get('/sims/export/excel',
        {
          params,
          responseType: 'blob'
        },
        {
          service: 'simService',
          action: 'export_sims_excel',
          entityType: 'sim',
          details: { exportParams: params }
        }
      );

      return response;
    } catch (error) {
      console.error('Error exporting sims to Excel:', error);
      throw error;
    }
  }
};

export default simService;